import { useEffect, useState } from "react";
import { Dish } from "./DishCard";

interface Props {
  open: boolean;
  dish: Dish | null;
  initialQuantity?: number;
  initialNote?: string;
  onClose: () => void;
  onAddToCart: (payload: { dish: Dish; quantity: number; note: string }) => void;
}

const EditDishModal = ({ open, dish, initialQuantity = 1, initialNote = "", onClose, onAddToCart }: Props) => {
  const [qty, setQty] = useState(initialQuantity);
  const [note, setNote] = useState(initialNote);

  useEffect(() => {
    if (open) {
      setQty(initialQuantity || 1);
      setNote(initialNote);
    }
  }, [open, initialQuantity, initialNote]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !dish) return null;
  const currency = dish.currency ?? "₹";

  return (
    <>
      <div role="dialog" aria-modal="true" aria-label={`Edit ${dish.name}`} className="fixed inset-0 z-[60] flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
        <div className="relative w-full max-w-[360px] bg-white rounded-[15px] overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.25)]" style={{ backgroundColor: "#FFFFFF" }} >
          {/* Top row: dish info + qty stepper */}
          <div className="flex items-start justify-between gap-3 px-4 pt-3 pb-3">
            <div className="flex flex-col gap-1.5 min-w-0">
              <p className="font-sansation font-bold text-[#111] text-[12px]">
                Dish Code : {dish.code}
              </p>
              <p className="font-opensans text-[14px] text-[rgba(17,17,17,0.8)]">
                {dish.name}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <p className="font-sansation font-bold text-[#111] text-[16px]">
                  {currency} {(dish.gross_price ?? dish.price).toFixed(2)}
                </p>
                {dish.gross_price !== undefined && dish.gross_price < dish.price && (
                  <div className="border border-[#2E7D32] rounded-[4px] px-2.5 py-1 text-[#2E7D32] font-sansation font-bold text-[15px] leading-none flex items-center justify-center">
                    {dish.price % 1 === 0 ? dish.price.toFixed(0) : dish.price.toFixed(2)}
                  </div>
                )}
              </div>
            </div>

            <div className="shrink-0 mt-1 h-[37px] w-[91px] rounded-[6px] border bg-white flex items-center justify-between px-3" style={{ borderColor: "#F15A29" }} >
              <button
                type="button"
                aria-label="Decrease quantity"
                onClick={() => setQty((n) => Math.max(1, n - 1))}
                className="font-outfit font-semibold text-[14px] bg-transparent border-0 leading-none"
                style={{ color: "#F15A29" }}
              >
                −
              </button>
              <span className="font-sansation font-bold text-[16px]" style={{ color: "#F15A29" }} >
                {qty}
              </span>
              <button
                type="button"
                aria-label="Increase quantity"
                onClick={() => setQty((n) => n + 1)}
                className="font-outfit font-semibold text-[14px] bg-transparent border-0 leading-none"
                style={{ color: "#F15A29" }}
              >
                +
              </button>
            </div>
          </div>

          <div className="h-px bg-[#E5E5E7] mx-0" />
          {/* Note */}
          <div className="px-4 pt-3 pb-4 flex flex-col gap-2">
            <label htmlFor="dish-note" className="font-opensans text-[14px] text-[#111]">Special Request</label>
            <textarea
              id="dish-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter Note"
              rows={3}
              className="w-full resize-none rounded-[6px] border bg-white px-4 py-3 font-opensans text-[14px] text-[#111] outline-none placeholder:text-[#8F9098] focus:border-[#F15A29]"
              style={{ borderColor: "#C5C6CC" }}
            />
          </div>

          {/* CTA */}
          <div className="px-4 pb-4">
            <button
              type="button"
              onClick={() => onAddToCart({ dish, quantity: qty, note })}
              className="w-full rounded-[6px] py-3.5 font-sansation font-bold text-[14px] tracking-[-0.28px] uppercase text-white border-0"
              style={{ backgroundColor: "#F15A29" }}
            >
              Add to cart
            </button>
          </div>
        </div>
      </div>
    </>

  );
};

export default EditDishModal;
